import { useEffect, useId, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { COMMENT_EMOJI } from './emojiSet';
import { GIF_SEARCH_FAILED, searchGifs } from './giphy';
import type { GifResult } from './giphy';

/**
 * The emoji and GIF buttons on the comment box toolbar (Messaging Drop 3b).
 *
 * Both are small popovers anchored to their button. Neither knows about the
 * editor: each reports a pick upward and CommentEditor does the inserting,
 * so the one place that writes into the document stays in CommentEditor.
 */

/**
 * A popover that closes on Escape or a click anywhere outside it. Exported
 * for the quick reactions picker (CommentReactions.tsx), so both behave alike.
 */
export function usePopover() {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    function onDown(event: MouseEvent) {
      if (wrapRef.current !== null && !wrapRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return { open, setOpen, wrapRef };
}

function ToolButton({
  label,
  open,
  disabled,
  onClick,
  controls,
  children,
}: {
  label: string;
  open: boolean;
  disabled: boolean;
  onClick: () => void;
  controls: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      className={`comment-tool${open ? ' comment-tool--on' : ''}`}
      aria-label={label}
      aria-haspopup="dialog"
      aria-expanded={open}
      aria-controls={open ? controls : undefined}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

export function EmojiButton({
  disabled,
  onPick,
}: {
  disabled: boolean;
  onPick: (emoji: string) => void;
}) {
  const { open, setOpen, wrapRef } = usePopover();
  const panelId = useId();

  return (
    <span className="comment-picker-wrap" ref={wrapRef}>
      <ToolButton
        label="Insert emoji"
        open={open}
        disabled={disabled}
        controls={panelId}
        onClick={() => setOpen((was) => !was)}
      >
        🙂
      </ToolButton>
      {open ? (
        <div
          id={panelId}
          className="comment-picker comment-emoji-picker"
          role="dialog"
          aria-label="Emoji"
        >
          <div className="comment-emoji-grid">
            {COMMENT_EMOJI.map((emoji) => (
              <button
                key={emoji.char}
                type="button"
                className="comment-emoji"
                aria-label={emoji.name}
                title={emoji.name}
                onClick={() => {
                  onPick(emoji.char);
                  setOpen(false);
                }}
              >
                {emoji.char}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </span>
  );
}

type GifState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'done'; results: GifResult[] }
  | { kind: 'error'; message: string };

export function GifButton({
  apiKey,
  disabled,
  onPick,
  search = searchGifs,
}: {
  apiKey: string;
  disabled: boolean;
  onPick: (gif: GifResult) => void;
  /** Injected in tests; the real one calls GIPHY. */
  search?: typeof searchGifs;
}) {
  const { open, setOpen, wrapRef } = usePopover();
  const panelId = useId();
  const [query, setQuery] = useState('');
  const [state, setState] = useState<GifState>({ kind: 'idle' });
  const requestRef = useRef<AbortController | null>(null);

  // A closed picker has nothing in flight.
  useEffect(() => {
    if (!open) {
      requestRef.current?.abort();
      requestRef.current = null;
    }
  }, [open]);

  function run() {
    const term = query.trim();
    if (term === '') {
      return;
    }
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    setState({ kind: 'loading' });
    void search(apiKey, term, controller.signal).then(
      (results) => {
        if (requestRef.current === controller) {
          setState({ kind: 'done', results });
        }
      },
      () => {
        if (requestRef.current === controller && !controller.signal.aborted) {
          setState({ kind: 'error', message: GIF_SEARCH_FAILED });
        }
      },
    );
  }

  return (
    <span className="comment-picker-wrap" ref={wrapRef}>
      <ToolButton
        label="Insert a GIF"
        open={open}
        disabled={disabled}
        controls={panelId}
        onClick={() => setOpen((was) => !was)}
      >
        GIF
      </ToolButton>
      {open ? (
        <div
          id={panelId}
          className="comment-picker comment-gif-picker"
          role="dialog"
          aria-label="GIFs"
        >
          {/* Not a <form>: the comment box may itself sit in one, and a
              nested form is invalid. Enter in this box searches and stops
              there, so it never reaches the comment box's Enter-to-send. */}
          <div className="comment-gif-search" role="search">
            <input
              type="search"
              className="comment-gif-input"
              aria-label="Search GIFs"
              placeholder="Search GIFs"
              value={query}
              // Opening the picker is asking to search; the box is ready.
              autoFocus
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  event.stopPropagation();
                  run();
                }
              }}
            />
            <button type="button" className="comment-gif-go" onClick={run}>
              Search
            </button>
          </div>
          {state.kind === 'loading' ? <p className="comment-gif-note">Searching…</p> : null}
          {state.kind === 'error' ? (
            <p className="comment-gif-note comment-gif-note--error" role="alert">
              {state.message}
            </p>
          ) : null}
          {state.kind === 'done' && state.results.length === 0 ? (
            <p className="comment-gif-note">No GIFs found. Try another word.</p>
          ) : null}
          {state.kind === 'done' && state.results.length > 0 ? (
            <div className="comment-gif-grid">
              {state.results.map((gif) => (
                <button
                  key={gif.id}
                  type="button"
                  className="comment-gif"
                  aria-label={gif.title}
                  title={gif.title}
                  onClick={() => {
                    onPick(gif);
                    setOpen(false);
                  }}
                >
                  <img src={gif.previewUrl} alt="" loading="lazy" referrerPolicy="no-referrer" />
                </button>
              ))}
            </div>
          ) : null}
          <p className="comment-gif-credit">Powered by GIPHY</p>
        </div>
      ) : null}
    </span>
  );
}
